import { Box, CircularProgress, TextField, Typography } from '@mui/material';
import { Field, Form, Formik, FormikHelpers } from 'formik';
import { useState } from 'react';
import * as Yup from 'yup';
import { ErrorSnackbar } from '../components/ErrorSnackbar';
import { FormHeaderImage, FormHeaderWrapper, FormLoadingOverlay, FormPaper } from '../components/FormStyledComponents';
import { PrivacyConsentSection } from '../components/PrivacyConsentSection';
import { useHeaderImage } from '../hooks/useHeaderImage';
import { LanscoButton } from '../lansco-button';
import { downloadFile } from '../utils/downloadFile';
import { FORM_IDS } from '../utils/formAssets';
import { submitFormToApi } from '../utils/submitForm';

// Form validation and types
interface FormValues {
  name: string;
  email: string;
  phone: string;
  company: string;
  consent: boolean;
}

const validationSchema = Yup.object({
  name: Yup.string().required('Name ist erforderlich'),
  email: Yup.string()
    .email('Ungültige E-Mail-Adresse')
    .matches(/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i, 'Ungültiges E-Mail-Format')
    .required('E-Mail ist erforderlich'),
  phone: Yup.string(),
  company: Yup.string(),
  consent: Yup.boolean()
    .required('Bitte stimmen Sie den Datenschutzbestimmungen zu')
    .oneOf([true], 'Sie müssen den Datenschutzbestimmungen zustimmen, um sich für das Webinar anzumelden'),
});

const initialValues: FormValues = {
  name: '',
  email: '',
  phone: '',
  company: '',
  consent: false,
};

// API endpoint
const API_ENDPOINT = 'https://3579fdf80154ea27b4d1f7c7d4a13a.57.environment.api.powerplatform.com:443/powerautomate/automations/direct/workflows/04f0ec94c48f479c9b8cf775c4cd7867/triggers/manual/paths/invoke?api-version=1&sp=%2Ftriggers%2Fmanual%2Frun&sv=1.0&sig=Ck8WalMfQwiIIzevCRey-msoBHqmh4Wgjq619d984d4';

export const WorldBackupDay2026 = () => {
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [showErrorSnackbar, setShowErrorSnackbar] = useState(false);
  const headerImage = useHeaderImage(FORM_IDS.WORLD_BACKUP_DAY_2026);

  const handleSubmit = async (
    values: FormValues,
    { setSubmitting }: FormikHelpers<FormValues>
  ) => {
    setIsLoading(true);
    setErrorMessage('');
    setShowErrorSnackbar(false);

    try {
      const result = await submitFormToApi({
        apiEndpoint: API_ENDPOINT,
        payload: {
          name: values.name,
          mail: values.email,
          phone: values.phone,
          company: values.company,
          // SharePoint path where the backend retrieves the ICS file for the confirmation email
          relativeFileUrl: '/WebinarICSFiles/2026-03%20World%20Backup%20Day/Webinar%20-%20World%20Backup%20Day%202026.ics',
          consent: values.consent,
        },
        debugFieldValue: values.name,
      });

      if (result.success) {
        setIsSubmitted(true);
      } else {
        setErrorMessage(result.error!);
        setShowErrorSnackbar(true);
      }
    } catch (err) {
      console.error('Error submitting form:', err);
      setErrorMessage('Beim Absenden des Formulars ist ein Fehler aufgetreten. Bitte versuchen Sie es erneut.');
      setShowErrorSnackbar(true);
    } finally {
      setIsLoading(false);
      setSubmitting(false);
    }
  };

  const handleDownload = () => {
    downloadFile({
      fileUrl: `${window.location.origin}/forms/worldbackupday2026/webinar-session.ics`,
      fileName: 'World-Backup-Day-2026-Webinar.ics',
      mimeType: 'text/calendar',
      redirectUrl: 'https://www.lansco.de/',
    });
  };

  return (
    <>
      <FormHeaderWrapper>
        <FormHeaderImage src={headerImage} alt="World Backup Day 2026" />
      </FormHeaderWrapper>
      <FormPaper elevation={3}>
        {!isSubmitted ? (
          <>
            <Typography variant="h4" component="h1" gutterBottom align="center" sx={{ fontWeight: 300, mb: 3, color: 'primary.main' }}>
              World Backup Day 2026 - Daten weg! Backup ready?
            </Typography>
            <Typography variant="body1" component="div" sx={{ mb: 2, lineHeight: 1.6 }}>
              Microsoft 365 ist zentraler Bestandteil des Arbeitsalltags – doch beim Thema Backup herrscht oft Unsicherheit. Was ist eigentlich abgesichert, wo liegen die Grenzen und worauf sollte man achten? Genau hier setzt dieses Live-Webinar in Kooperation mit AvePoint zum World Backup Day 2026 an.
            </Typography>
            <Typography variant="body1" component="div" sx={{ mb: 2, lineHeight: 1.6 }}>
              Wir geben einen verständlichen Überblick, warum Backup in Microsoft 365 wichtig ist, welche typischen Annahmen nicht ganz stimmen und wie sich das Thema pragmatisch angehen lässt. Anhand von Beispielen und einer kurzen Demo zeigen wir, wie Backup in der Praxis aussehen kann – ohne tief ins Technische abzutauchen.
            </Typography>
            <Typography variant="h6" component="h2" sx={{ mb: 1, fontWeight: 'bold', color: 'primary.main' }}>
              Das nimmst Du mit:
            </Typography>
            <Typography variant="body2" component="ul" sx={{ mb: 2, pl: 2, '& li': { mb: 1 } }}>
              <li>Ein besseres Gefühl dafür, warum Backup in M365 relevant ist</li>
              <li>Einen einfachen Einblick, wie Backup und Wiederherstellung aussehen können</li>
              <li>Orientierung, womit man sich beim Thema Backup beschäftigen sollte</li>
            </Typography>
            {isLoading && (
              <FormLoadingOverlay>
                <CircularProgress size={60} sx={{ color: 'primary.main' }} />
              </FormLoadingOverlay>
            )}
            <Formik
              initialValues={initialValues}
              validationSchema={validationSchema}
              onSubmit={handleSubmit}
              validateOnMount={true}
              initialTouched={{
                consent: true
              }}
            >
              {({ errors, touched, isValid, dirty }) => (
                <Form>
                  <Field
                    as={TextField}
                    fullWidth
                    margin="normal"
                    name="name"
                    label="Name"
                    error={touched.name && Boolean(errors.name)}
                    helperText={touched.name && errors.name}
                    sx={{ mb: 2 }}
                    disabled={isLoading}
                  />
                  <Field
                    as={TextField}
                    fullWidth
                    margin="normal"
                    name="email"
                    label="E-Mail"
                    error={touched.email && Boolean(errors.email)}
                    helperText={touched.email && errors.email}
                    sx={{ mb: 2 }}
                    disabled={isLoading}
                  />
                  <Field
                    as={TextField}
                    fullWidth
                    margin="normal"
                    name="phone"
                    label="Telefonnummer"
                    error={touched.phone && Boolean(errors.phone)}
                    helperText={touched.phone && errors.phone}
                    sx={{ mb: 2 }}
                    disabled={isLoading}
                  />
                  <Field
                    as={TextField}
                    fullWidth
                    margin="normal"
                    name="company"
                    label="Unternehmen"
                    error={touched.company && Boolean(errors.company)}
                    helperText={touched.company && errors.company}
                    sx={{ mb: 2 }}
                    disabled={isLoading}
                  />
                  <PrivacyConsentSection
                    label="Ja, ich stimme zu und möchte mich für das Webinar anmelden"
                    isLoading={isLoading}
                  />
                  {errorMessage && (
                    <Typography color="error" variant="body2" sx={{ mt: 2 }}>
                      {errorMessage}
                    </Typography>
                  )}

                  <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
                    <LanscoButton
                      type="submit"
                      disabled={!(isValid && dirty) || isLoading}
                      sx={{ minWidth: 180 }}
                    >
                      {isLoading ? <CircularProgress size={24} color="inherit" /> : 'Für Webinar anmelden'}
                    </LanscoButton>
                  </Box>
                </Form>
              )}
            </Formik>
          </>
        ) : (
          <Box sx={{ py: 4, textAlign: "center" }}>
            <Typography variant="h4" component="h1" gutterBottom sx={{ color: 'primary.main', fontWeight: 300 }}>
              Vielen Dank für Deine Anmeldung!
            </Typography>
            <Typography variant="body1" component="p" sx={{ mb: 4 }}>
              Deine Anmeldung wurde erfolgreich empfangen. Lade Dir jetzt den Kalendertermin herunter, damit Du das Webinar nicht verpasst.
            </Typography>
            <LanscoButton
              // @ts-ignore - Custom variant and HTML anchor props
              variant='white'
              component="a"
              onClick={handleDownload}
              sx={{ minWidth: 220 }}
            >
              Kalendertermin herunterladen
            </LanscoButton>
          </Box>
        )}
      </FormPaper>
      <ErrorSnackbar
        open={showErrorSnackbar}
        message={errorMessage}
        onClose={() => setShowErrorSnackbar(false)}
      />
    </>
  );
};
